import { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Account from "../../models/account";
import accountService from "../../services/accountService";
import formHelper from "../../util/formHelper";
import AddressEdit from "../../components/common/AddressEdit";
import Address from "../../models/address";
import ModalWindow from "../../components/common/ModalWindow";
import ValidationManager from "../../components/common/validationManager";
import Validator from "../../components/common/Validator";
import User from "../../models/user";
import UserContext from "../../contexts/userContext";
import SubscriptionContextModel from "../../contexts/subscriptionContextModel";
import SubscriptionContext from "../../contexts/subscriptionContext";
import Page from "../../components/work/layout/Page";

const validator = {
  accountName: "accountName",
  currency: "currency",
  accountCode: "accountCode",
  email: "email",
  phone: "phone",
  billingDepartmentEmail: "billingDepartmentEmail",
};

const AccountEdit: React.FC = () => {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const user = useContext<User | undefined>(UserContext);
  const { subscription } =
    useContext<SubscriptionContextModel>(SubscriptionContext);

  const [isNew, setIsNew] = useState<boolean>(true);
  const [showDeleteWindow, setShowDeleteWindow] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [account, setAccount] = useState<Account>(new Account());

  const [currencies, setCurrencies] = useState<string[]>([]);

  const [validationManager] = useState<ValidationManager>(
    new ValidationManager(),
  );

  useEffect(() => {
    const initEdit = async (idAccount: number) => {
      try {
        const { data: account } = await accountService.getAccountById(
          subscription.idSubscription,
          idAccount,
        );

        if (account) {
          setAccount(account);

          validationManager.setIsValid(validator.accountName, true);
          validationManager.setIsValid(validator.currency, true);
          validationManager.setIsValid(validator.accountCode, true);
          validationManager.setIsValid(validator.email, true);
          validationManager.setIsValid(validator.phone, true);
          validationManager.setIsValid(validator.billingDepartmentEmail, true);
        }
      } catch (error) {
        // TODO: show error with toastr
      }
    };

    const currencies = getCurrencies();
    setCurrencies(currencies);

    const isNew = idParam === "new";
    setIsNew(isNew);

    if (!isNew) {
      const idAccount = Number(idParam);
      initEdit(idAccount);
    }
  }, [idParam, validationManager, subscription]);

  const getCurrencies = (): string[] => {
    const currencies = ["USD", "CAD"];
    return currencies;
  };

  const getFieldAndValueByEvent = ({ id, name, type, checked, value }: any) => {
    const fieldValue = type === "checkbox" ? checked : value;
    return {
      value: fieldValue,
      id: id,
    };
  };

  const onChange = (event: any) => {
    const { id, value } = getFieldAndValueByEvent(event.target);
    const newAccount = { ...account, [id]: value };
    setAccount(newAccount);

    if (id === validator.accountName) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.accountName, isValid);
    }
    if (id === validator.currency) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.currency, isValid);
    }
    if (id === validator.accountCode) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.accountCode, isValid);
    }

    if (id === validator.email) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.email, isValid);
    }

    if (id === validator.phone) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.phone, isValid);
    }

    if (id === validator.billingDepartmentEmail) {
      const isValid = value.length !== 0;
      validationManager.setIsValid(validator.billingDepartmentEmail, isValid);
    }
  };

  const onBillingAddressChange = (address: Address) => {
    const newAccount = { ...account, billingAddress: address };
    setAccount(newAccount);
  };

  const onShippingAddressChange = (address: Address) => {
    const newAccount = { ...account, shippingAddress: address };
    setAccount(newAccount);
  };

  const onSubmit = async (event: any) => {
    event.preventDefault();

    setErrorMessage("");

    if (!validationManager.validate()) {
      return;
    }

    try {
      if (isNew) {
        setIsBusy(true);

        if (user) {
          const newAccount = { ...account };
          setAccount(newAccount);

          await accountService.createAccount(
            subscription.idSubscription,
            newAccount,
          );
          setIsBusy(false);
        }
      } else {
        setIsBusy(true);
        await accountService.updateAccount(
          subscription.idSubscription,
          account,
        );
        setIsBusy(false);
      }
    } catch (error: any) {
      setIsBusy(false);
      const errorMessage = error.response.data;
      setErrorMessage(errorMessage);
      return;
    }

    navigate("/work/accounts");
  };

  const onDeleteConfirm = () => {
    setShowDeleteWindow(true);
  };

  const onDeleteOk = () => {
    setShowDeleteWindow(false);
    onDelete();
  };

  const onDeleteCancel = () => {
    setShowDeleteWindow(false);
  };

  const onDelete = async () => {
    setIsBusy(true);
    await accountService.deleteAccount(
      subscription.idSubscription,
      account.idAccount,
    );
    setIsBusy(false);
    navigate("/work/accounts");
  };

  const onCancel = () => {
    navigate("/work/accounts");
  };

  return (
    <Page
      pageTitle={(isNew ? "Create" : "Edit") + " Account"}
      breadcrumbs={[
        { linkName: "Accounts", linkUlr: "/work/accounts" },
        { linkName: isNew ? "Create" : "Edit", linkUlr: "" },
      ]}
    >
      <section className="section">
        <div className="card p-4">
          <form
            className="row g-3"
            onSubmit={onSubmit}
            onKeyDown={formHelper.onEnterKeyDownPreventSubmit}
          >
            <div className="col-md-6">
              <Validator
                name={validator.accountName}
                errorMessage="Please enter an account name."
                validationManager={validationManager}
              >
                <div>
                  <label htmlFor="accountName" className="form-label">
                    Account Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="accountName"
                    value={account.accountName ?? ""}
                    onChange={onChange}
                  />
                </div>
              </Validator>
            </div>

            <div className="col-md-3">
              <Validator
                name={validator.accountCode}
                errorMessage="Please enter an unique account code."
                validationManager={validationManager}
              >
                <div>
                  <label htmlFor="accountCode" className="form-label">
                    Account Code
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="accountCode"
                    value={account.accountCode ?? ""}
                    onChange={onChange}
                  />
                </div>
              </Validator>
            </div>

            <div className="col-md-3">
              <Validator
                name={validator.currency}
                errorMessage="Please enter a currency."
                validationManager={validationManager}
              >
                <div>
                  <label htmlFor="currency" className="form-label">
                    Currency
                  </label>
                  <select
                    className="form-select"
                    aria-label="select"
                    id="currency"
                    value={account.currency ?? ""}
                    onChange={onChange}
                  >
                    <option value="" disabled>
                      Please select
                    </option>
                    {currencies.map((currency, index) => (
                      <option key={index.toString()} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                </div>
              </Validator>
            </div>

            <div className="col-md-6">
              <Validator
                name={validator.email}
                errorMessage="Please enter an email address."
                validationManager={validationManager}
              >
                <div>
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="email"
                    value={account.email ?? ""}
                    onChange={onChange}
                  />
                </div>
              </Validator>
            </div>

            <div className="col-md-6">
              <Validator
                name={validator.phone}
                errorMessage="Please enter a phone number."
                validationManager={validationManager}
              >
                <div>
                  <label htmlFor="phone" className="form-label">
                    Phone
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="phone"
                    value={account.phone ?? ""}
                    onChange={onChange}
                  />
                </div>
              </Validator>
            </div>

            <div className="col-md-6">
              <Validator
                name={validator.billingDepartmentEmail}
                errorMessage="Please enter an email address."
                validationManager={validationManager}
              >
                <div>
                  <label
                    htmlFor="billingDepartmentEmail"
                    className="form-label"
                  >
                    Billing Department Email
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="billingDepartmentEmail"
                    value={account.billingDepartmentEmail ?? ""}
                    onChange={onChange}
                  />
                </div>
              </Validator>
            </div>

            <div className="col-12">
              <label htmlFor="serviceInstructions" className="form-label">
                Service Instructions
              </label>
              <textarea
                className="form-control"
                id="serviceInstructions"
                value={account.serviceInstructions ?? ""}
                onChange={onChange}
                style={{ height: "100px" }}
              />
            </div>

            <div>
              <label className="form-label">
                <b>Billing Address</b>
              </label>
            </div>

            <div>
              <AddressEdit
                inputAddress={account.billingAddress}
                onChange={onBillingAddressChange}
                isNew={isNew}
                validationManager={validationManager}
                validationId="billing"
              />
            </div>

            <div className="d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                checked={account.isShippingSameAsBillingAddress}
                id="isShippingSameAsBillingAddress"
                onChange={onChange}
              />
              <label className="ms-2" htmlFor="isShippingSameAsBillingAddress">
                Shipping Address same as Billing Address
              </label>
            </div>

            {!account.isShippingSameAsBillingAddress && (
              <>
                <div>
                  <label className="form-label">
                    <b>Shipping Address</b>
                  </label>
                </div>
                <div>
                  <AddressEdit
                    inputAddress={account.shippingAddress}
                    onChange={onShippingAddressChange}
                    isNew={isNew}
                    validationManager={validationManager}
                    validationId="shipping"
                  />
                </div>
              </>
            )}

            <div className="col-12">
              <div className="d-flex justify-content-between">
                <div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isBusy}
                  >
                    {isNew ? "Create" : "Save"}
                  </button>{" "}
                  <button
                    type="reset"
                    className="btn btn-secondary"
                    onClick={() => onCancel()}
                  >
                    Cancel
                  </button>
                </div>
                {!isNew && (
                  <div>
                    <button
                      type="reset"
                      className="btn btn-danger"
                      onClick={() => onDeleteConfirm()}
                      disabled={isBusy}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>

            {errorMessage !== "" && (
              <div className="row text-center">
                <div className="error-message">{errorMessage}</div>
              </div>
            )}
          </form>
        </div>
      </section>
      {showDeleteWindow && (
        <ModalWindow title="Delete" onOk={onDeleteOk} onCancel={onDeleteCancel}>
          <div>{`Are you sure you want to delete the account?`}</div>
        </ModalWindow>
      )}
    </Page>
  );
};

export default AccountEdit;
